import { FC, ReactNode, useState, useContext } from 'react';
import getConfig from 'next/config';
import DefaultLink from 'next/link';
import classNames from 'classnames';
import { StaticGoogleMap, Marker } from 'react-static-google-map';

import { i18n as I18n } from 'utils/i18n';
import { filteredKeyFacets } from 'utils/filters';

// context
import { AuthContext } from 'state/auth';

import useFavourite from 'hooks/useFavourite';
import { Discount as IDiscount } from 'hooks/usePropertyDiscount';
import { Venue as VenueType } from 'hooks/useVenue';

// components
import { FavouriteButton } from 'components/shared/favouriteButton';
import FeatureIcon from 'components/shared/featureIcon';
import FloorplansButton from 'components/floorplansButton';
import Discount from 'components/discount';
import DiscountTermsModal from 'components/discountTermsModal';

const { publicRuntimeConfig: { googleMapsApiKey } = {} } = getConfig();

const Link: typeof DefaultLink = ({ children, ...props }) => {
  return (
    <DefaultLink {...props}>
      <a>{children}</a>
    </DefaultLink>
  );
};

interface Props {
  id: VenueType['id'];
  name: VenueType['name'];
  url: string;
  distance?: VenueType['distance'];
  latLng?: VenueType['latLng'];
  image?: string;
  location?: string;
  showSpacesDefault?: boolean;
  spaces?: () => ReactNode;
  facets?: VenueType['facets'];
  floorplans?: VenueType['floorplans'];
  discount?: IDiscount;
  onClickMap: (id: VenueType['id']) => void;
  linkComponent?: typeof Link;
  venueSpaces: [];
  organisationName?: string;
}

const Venue: FC<Props> = ({
  distance = '0.0', // FIXME: This should be a number.
  id,
  name,
  url,
  image,
  latLng,
  location,
  showSpacesDefault = true,
  spaces = () => null,
  facets = [],
  floorplans,
  discount = undefined,
  discounts = [],
  onClickMap,
  linkComponent: LinkComponent = Link,
  venueSpaces = [],
  organisationName,
}) => {
  const [showSpaces, toggleSpaces] = useState(showSpacesDefault);
  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] = useState(
    false
  );
  const { loggedIn } = useContext(AuthContext);

  const [favourited, toggleFavourite] = useFavourite('venues', id);

  const toggleClasses = classNames('venue__toggle', {
    'venue__toggle--open': !!showSpaces,
  });

  const renderBgImage = () =>
    image ? { backgroundImage: `url(${image})` } : null;

  const displayDiscount = discount => {
    const categoryIds = [
      ...new Set(venueSpaces.map(space => space.categoryIds).flat()),
    ];

    const roomIds = [...new Set(venueSpaces.map(space => space.id).flat())];

    const categoryMatches = categoryIds.some(categoryId =>
      discount.roomTypeIds.includes(categoryId)
    );

    const roomMatches = roomIds.some(roomId =>
      discount.spaceIds.includes(roomId)
    );

    const venueMatches = discount.venueIds.includes(id);

    return (
      venueMatches ||
      discount.venueIds.length === 0 ||
      (categoryMatches && venueMatches) ||
      (roomMatches && (venueMatches || discount.venueIds.length === 0))
    );
  };

  return (
    <li key={`${name}-${id}`} className="venue" data-cy="property">
      <div className="venue__inner">
        <div className="venue__image" style={renderBgImage()}>
          {!image && (
            <div>
              Image
              <br />
              coming soon
            </div>
          )}
        </div>
        <div
          className="venue__map"
          title={I18n.t('venue_list.view_venue_on_map')}
          onClick={() => onClickMap(id)}
        >
          {!!latLng && (
            <StaticGoogleMap size="153x138" zoom={14} apiKey={googleMapsApiKey}>
              <Marker location={latLng} color="blue" />
            </StaticGoogleMap>
          )}
          {!latLng && (
            <div>
              Map
              <br />
              coming soon
            </div>
          )}
        </div>
        <div className="venue__info">
          {loggedIn && (
            <FavouriteButton
              favouritedState={favourited}
              type="venues"
              handleFavouriteButtonClick={toggleFavourite}
            />
          )}

          <h3 className="venue__name">
            <LinkComponent href={url}>{name}</LinkComponent>
          </h3>
          {organisationName && (
            <h4 className="venue__organisation-row">
              <span>{I18n.t('shared.managed_by')}:&nbsp;</span>
              {`${organisationName}`}
            </h4>
          )}

          {!!distance && !!location && (
            <p className="venue__distance">
              {I18n.t('search_results.miles_from_location', {
                count: distance,
                location,
              })}
            </p>
          )}

          <ul className="venue__feature-list">
            <FloorplansButton venue={{ id, floorplans }} wrapper="li" />

            {filteredKeyFacets(facets).map(facet => (
              <li key={facet.name}>
                <FeatureIcon
                  name={facet.name}
                  icon={facet.icon}
                  iconAltText={facet.iconAltText}
                  displaySvgIcon={false}
                />
              </li>
            ))}
          </ul>

          {discount && displayDiscount(discount) && (
            <>
              <Discount
                text={I18n.t('discounts.save_up_to', {
                  percent: discount.amount,
                })}
                rounded
                onClick={() => setDiscountTermsModalIsOpen(true)}
              />

              <DiscountTermsModal
                isOpen={discountTermsModalIsOpen}
                onClose={() => setDiscountTermsModalIsOpen(false)}
                propertyOrRoomName={name}
                discounts={discounts}
              />
            </>
          )}

          <button
            className={toggleClasses}
            onClick={() => toggleSpaces(!showSpaces)}
          >
            {showSpaces ? 'Hide rooms' : 'Show rooms'}
          </button>
        </div>
      </div>

      {showSpaces && spaces()}
    </li>
  );
};

export default Venue;
